/**
 * Wave 2 SEO cluster: LatAm vocabulary — hoja de vida vs currículum by country.
 * Spanish and English. Merged from posts.js.
 */

export default [
  {
    slug: 'hoja-de-vida-o-curriculum',
    date: '2026-08-22',
    category: 'format',
    related: ['hoja-de-vida-ats', 'cv-ingles-o-espanol', 'cv-mexico-formato'],
    es: {
      title: 'Hoja de vida o currículum: diferencias en España y Latinoamérica',
      description:
        '¿Hoja de vida o currículum vitae? Mismo documento, nombres distintos: foto, DNI, RFC y ATS en Colombia, México y España. Cómo adaptar el PDF que ya tienes.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'Hoja de vida o currículum no son dos géneros distintos. Son el mismo tipo de archivo con el nombre que usa tu mercado. En Colombia (y en parte de Ecuador, Venezuela y Centroamérica) se busca “hoja de vida”. En España, “currículum” o “CV”. En México conviven “currículum”, “CV” y, menos, “hoja de vida”. El ATS de una multilatina no premia cómo lo llames. Premia texto seleccionable, fechas y keywords de la vacante. Si ya tienes un PDF, el trabajo no es reescribir tu vida: es decidir qué campos viajan al país de destino.',
        },
        {
          type: 'p',
          text: 'Esta guía no pelea “cómo hacer una hoja de vida” contra las fábricas de plantillas. Cubre el mapa de nombres, el formato privado frente al único de empleo público en Colombia, qué quitar si aplicas a España o a un proceso US, y cuándo la foto o la cédula ayudan o hunden. El hermano técnico —columnas, Canva, parser— está en [hoja de vida compatible con ATS](/blog/hoja-de-vida-ats). El idioma del documento (español vs inglés) en [CV en inglés o en español](/blog/cv-ingles-o-espanol). ClarCV (clarcv.com) encaja cuando el archivo ya existe: lo subes, limpias campos de más y regeneras una plantilla A4 parseable.',
        },
        {
          type: 'p',
          text: 'No vas a “traducir” hoja de vida a currículum palabra por palabra. Vas a quitar la cédula del PDF que se reenvía, a decidir la foto según el país, y a dejar el mismo logro en una columna. El nombre del archivo (HV_Juan.pdf o Ana_Lopez_CV.pdf) es cosmética. El parseo no lo es.',
        },
        { type: 'h2', text: 'Cómo se llama el documento (y quién lo busca)' },
        {
          type: 'p',
          text: 'Usa en el asunto, el nombre de archivo y el H1 interno la palabra que usa el anuncio. Si Computrabajo dice “adjunta tu hoja de vida”, el email puede decir hoja de vida. Si InfoJobs dice “currículum”, di currículum. El contenido puede ser idéntico. Mezclar “resume” americano en un proceso 100 % español solo aporta ruido, salvo que la oferta esté en inglés.',
        },
        {
          type: 'ul',
          items: [
            'Colombia: hoja de vida (privado) o formato único / SIGEP (empleo público). No los confundas.',
            'España: currículum / CV. Europass solo si el pliego o la movilidad europea lo piden.',
            'México: currículum o CV. “Hoja de vida” se entiende, pero no es el término de portales grandes.',
            'Argentina, Chile, Perú, Uruguay: CV / currículum. Foto y DNI según costumbre local, no según Colombia.',
            'US / UK / Canadá: résumé o CV (académico). Sin foto, sin ID, sin estado civil. Carta aparte.',
          ],
        },
        {
          type: 'p',
          text: 'Si aplicas desde Bogotá a Madrid, el reclutador español no necesita que el PDF se titule “hoja de vida”. Espera un CV de una o dos páginas, ciudad, teléfono internacional, LinkedIn. Si aplicas desde Madrid a Medellín, “currículum” se entiende; “hoja de vida” encaja mejor en el asunto. El cuerpo del archivo: los mismos hechos.',
        },
        { type: 'h2', text: 'Colombia: privado vs formato único (DAFP / SIGEP)' },
        {
          type: 'p',
          text: 'El “formato hoja de vida Colombia” que aparece en Google suele ser un Word para empresa privada: foto, datos personales, experiencia, estudios. Eso no es el Formato Único de Hoja de Vida de empleo público. Si el concurso o la entidad piden el formato del Departamento Administrativo de la Función Pública o cargarlo en SIGEP, ese pliego manda. No sustituyas ese PDF oficial por un Canva “moderno”. Lo desestiman por forma, no por méritos.',
        },
        {
          type: 'p',
          text: 'Para empresa privada, multilatina o remoto, el formato único suele ser peor que un PDF de una columna: demasiados campos, cédula a la vista, tablas. Extrae logros y herramientas a un CV parseable. Conserva el formato oficial en una carpeta para cuando lo exijan. Misma lógica que [Europass o CV moderno](/blog/europass-o-cv-moderno): el canal decide el continente, no tu biografía.',
        },
        {
          type: 'ul',
          items: [
            'Empleo público colombiano con formato obligatorio: usa el oficial. No improvises.',
            'Privado / Computrabajo / LinkedIn Easy Apply: PDF ligero, texto seleccionable, sin cédula en la cabecera.',
            'Cédula: el portal puede pedirla en un campo. El PDF que se reenvía a diez personas, no.',
            'Pretensión salarial: en el formulario del anuncio, no como línea eterna del archivo.',
          ],
        },
        { type: 'h2', text: 'México: currículum, RFC y la foto' },
        {
          type: 'p',
          text: 'En México el documento de empresa privada es un currículum. El RFC y el CURP no van en el PDF de candidatura masiva; irán al alta si te contratan. La foto sigue siendo habitual en muchos procesos locales; en una filial US o en un ATS global, quítala. Dirección: ciudad y alcaldía o municipio, no calle y número. Si el anuncio está en inglés para una empresa de Monterrey o CDMX con reporting a Dallas, el archivo en inglés y sin foto suele ser más seguro. Detalle de foto por país: [foto en el CV](/blog/foto-en-el-cv).',
        },
        {
          type: 'p',
          text: '“Hoja de vida” en un asunto hacia un reclutador mexicano no es un error grave; “currículum” encaja más. Lo que sí es un error es un infográfico de Canva con el teléfono en un icono: el parser mexicano usa las mismas reglas. Ver [iconos en el CV](/blog/iconos-en-el-cv).',
        },
        { type: 'h2', text: 'España: currículum, sin DNI, MCER' },
        {
          type: 'p',
          text: 'En España di currículum o CV. No pongas DNI ni fecha de nacimiento en el PDF. Idiomas en A1–C2, no “inglés alto”. Foto: todavía se espera en muchos procesos locales; en consultoras globales y en ofertas UK, no. Guía de campos: [qué no poner en un CV](/blog/que-no-poner-en-el-cv). Si tu hoja de vida colombiana trae estado civil, cédula y tres páginas, recorta antes de InfoJobs. El ATS español no busca “hoja de vida”; busca Experiencia, Formación, el email en texto.',
        },
        {
          type: 'p',
          text: 'Teléfono: prefijo internacional (+57, +52, +34). Ciudad: “Bogotá, disponible para relocarse a Madrid” es una línea útil; la calle no. LinkedIn en URL de texto, no en QR.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'El nombre cambia. El parser no',
          lead: 'Da igual si el anuncio dice hoja de vida o currículum. Si el PDF tiene columnas, cédula o iconos, falla en Bogotá y en Madrid. Regenera una versión parseable del archivo que ya tienes.',
          from: 'Hoja de vida con cédula',
          to: 'CV / HV que se puede leer',
          button: 'Adaptar mi archivo',
        },
        { type: 'h2', text: 'Tabla rápida: qué viaja y qué se queda' },
        {
          type: 'p',
          text: 'Mismo contenido de logros y fechas. Cambia la cabecera y los extras. Antes de enviar a otro país, pasa esta lista sobre el PDF que ya tienes — no hace falta un documento nuevo desde cero.',
        },
        {
          type: 'ul',
          items: [
            'Nombre del documento: el del anuncio (hoja de vida / currículum / CV / résumé).',
            'Identificación: cédula, DNI, RFC, CURP — fuera del PDF privado; sí en formularios o formato público oficial.',
            'Foto: LatAm y España a menudo sí; US/UK/Canadá no. Una foto antigua perjudica más que ninguna.',
            'Estado civil, hijos, religión: fuera en todos los destinos corporativos.',
            'Idiomas: MCER (A1–C2) en Europa y en multilatinas; “fluent” solo no basta en España.',
            'Extensión: 1 página junior; 2 senior. El formato único público puede ser más largo porque el pliego lo exige.',
            'Tamaño de página: A4 para España y LatAm. Letter si el proceso es 100 % US.',
          ],
        },
        {
          type: 'p',
          text: 'Antes (HV Colombia privada hacia un ATS en Madrid): foto grande, cédula, “pretensión 4.5 M”, tres columnas Canva. Después: Ana López, Madrid (reloc. desde Bogotá), teléfono +57…, LinkedIn; Experiencia con cifras; Inglés B2; PDF de una columna. Los hechos no cambiaron. Cambió lo que el filtro y el sesgo ven primero.',
        },
        { type: 'h2', text: 'Cómo adaptar el archivo que ya tienes (15 minutos)' },
        {
          type: 'p',
          text: 'Abre el PDF. Prueba del bloc de notas: nombre, email, tres viñetas. Si el texto no sale, el destino da igual: primero hazlo parseable ([hoja de vida ATS](/blog/hoja-de-vida-ats)). Luego borra identificación y estado civil, ajusta foto, pon prefijo, cambia el nombre de archivo a Nombre_Apellido_Puesto.pdf. Si el anuncio está en otro idioma, no traduzcas con Google a ciegas: alinea títulos al mercado de destino y conserva cifras. Detalle EN/ES: guía de idioma del CV.',
        },
        {
          type: 'p',
          text: 'En ClarCV subes el Word o PDF que ya usas en tu país. El análisis señala claridad y fricción de estructura (no un porcentaje mágico contra un ATS concreto). Aplicas una plantilla A4 con secciones estándar y descargas. Empieza gratis: 3 análisis y 2 descargas / 30 días, sin tarjeta. Pro 7 €/mes. Tú confirmas cédula y foto: la herramienta no debe inventar un DNI ni una nacionalidad.',
        },
        { type: 'h2', text: 'Portales: Computrabajo, InfoJobs, OCC, LinkedIn' },
        {
          type: 'p',
          text: 'Cada portal pide “su” palabra. El adjunto debe ser el mismo tipo de archivo limpio. Easy Apply de LinkedIn usa el PDF que subes al perfil: mantenlo parseable, no el Canva de la feria. Si el empleo público colombiano pide el formato único, no envíes el CV moderno “por si acaso” como único archivo: envía lo que el pliego nombra. Más sobre qué archivo va a cada canal: [CV para InfoJobs, LinkedIn y portales](/blog/cv-para-portales-empleo).',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Hoja de vida y currículum tienen distinta estructura?',
              a: 'En privado, no deberían. Experiencia, formación, habilidades, idiomas. La diferencia suele ser folklore de plantilla (cédula, foto, tablas), no el género documental.',
            },
            {
              q: '¿Puedo usar la misma hoja de vida en Colombia y en España?',
              a: 'Los logros sí. La cabecera no: quita cédula, ajusta foto y teléfono, nombra el archivo como CV si el anuncio es español. El parser es el mismo tipo de software.',
            },
            {
              q: '¿El formato único de Colombia sirve para una empresa privada?',
              a: 'Casi nunca es la mejor opción. Guárdalo para cuando lo exijan. Para privado, un PDF de una columna parseable.',
            },
            {
              q: '¿En México debo poner el RFC en el currículum?',
              a: 'No en el PDF que circula. El alta lo pedirá después. Igual que el DNI en España.',
            },
            {
              q: '¿ClarCV genera el formato SIGEP?',
              a: 'No. Genera plantillas A4 de CV/HV para procesos privados y ATS. El formato público oficial lo define la administración; no lo sustituyas.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Hoja de vida vs CV: differences in Spain and Latin America',
      description:
        'Same document, different names. Photo, national ID, RFC, and ATS in Colombia, Mexico, and Spain — how to adapt the PDF you already have.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'Hoja de vida versus currículum are not two different genres. They are the same kind of file under the name your market uses. In Colombia (and parts of Ecuador, Venezuela, and Central America) people search “hoja de vida”. In Spain, “currículum” or “CV”. In Mexico, “currículum” and “CV” coexist; “hoja de vida” is less common. A multilatina’s ATS does not reward what you call it. It rewards selectable text, dates, and vacancy keywords. If you already have a PDF, the job is not rewriting your life: it is deciding which fields travel to the destination country.',
        },
        {
          type: 'p',
          text: 'This guide does not fight “how to make a hoja de vida” against template mills. It maps the names, private format vs Colombia’s official public-employment form, what to strip if you apply to Spain or a US process, and when a photo or ID number helps or sinks you. The technical sibling — columns, Canva, parsers — is [ATS-friendly hoja de vida](/blog/hoja-de-vida-ats). Document language (Spanish vs English) is [CV in English or Spanish](/blog/cv-ingles-o-espanol). ClarCV (clarcv.com) fits when the file already exists: upload, strip extra fields, regenerate a parseable A4 template.',
        },
        {
          type: 'p',
          text: 'You will not “translate” hoja de vida into currículum word for word. You will take the ID number off the PDF that gets forwarded, decide the photo by country, and keep the same achievement in one column. The filename (HV_Juan.pdf or Ana_Lopez_CV.pdf) is cosmetic. Parsing is not.',
        },
        { type: 'h2', text: 'What the document is called (and who searches it)' },
        {
          type: 'p',
          text: 'In the subject line, filename, and internal heading, use the word the ad uses. If Computrabajo says “attach your hoja de vida”, the email can say hoja de vida. If InfoJobs says “currículum”, say currículum. The content can be identical. Mixing an American “resume” into a fully Spanish process only adds noise, unless the posting is in English.',
        },
        {
          type: 'ul',
          items: [
            'Colombia: hoja de vida (private) or unique / SIGEP form (public employment). Do not confuse them.',
            'Spain: currículum / CV. Europass only if the spec or EU mobility asks for it.',
            'Mexico: currículum or CV. “Hoja de vida” is understood, but not the term on large boards.',
            'Argentina, Chile, Peru, Uruguay: CV / currículum. Photo and ID follow local habit, not Colombia’s.',
            'US / UK / Canada: résumé or academic CV. No photo, no ID, no marital status. Cover letter separate.',
          ],
        },
        {
          type: 'p',
          text: 'If you apply from Bogotá to Madrid, the Spanish recruiter does not need the PDF titled “hoja de vida”. They expect a one- or two-page CV, city, international phone, LinkedIn. If you apply from Madrid to Medellín, “currículum” is understood; “hoja de vida” fits the subject better. File body: the same facts.',
        },
        { type: 'h2', text: 'Colombia: private vs unique form (DAFP / SIGEP)' },
        {
          type: 'p',
          text: 'The “Colombia hoja de vida format” that ranks on Google is often a Word file for private companies: photo, personal data, experience, studies. That is not the official Unique Hoja de Vida form for public employment. If the contest or entity asks for the Departamento Administrativo de la Función Pública form or a SIGEP upload, that spec wins. Do not replace that official PDF with “modern” Canva. They reject on form, not on merit.',
        },
        {
          type: 'p',
          text: 'For a private company, multilatina, or remote role, the official form is usually worse than a one-column PDF: too many fields, ID on display, tables. Extract achievements and tools into a parseable CV. Keep the official form in a folder for when they require it. Same logic as [Europass or a modern CV](/blog/europass-o-cv-moderno): the channel picks the container, not your biography.',
        },
        {
          type: 'ul',
          items: [
            'Colombian public employment with a mandatory form: use the official one. Do not improvise.',
            'Private / Computrabajo / LinkedIn Easy Apply: light PDF, selectable text, no ID in the header.',
            'National ID: the portal can ask in a field. The PDF forwarded to ten people should not.',
            'Salary expectation: in the ad’s form, not as a permanent line on the file.',
          ],
        },
        { type: 'h2', text: 'Mexico: currículum, RFC, and the photo' },
        {
          type: 'p',
          text: 'In Mexico the private-company document is a currículum. RFC and CURP do not belong on a mass-application PDF; they go into onboarding if you are hired. A photo is still common in many local processes; at a US subsidiary or a global ATS, remove it. Address: city and borough or municipality, not street number. If the ad is in English for a Monterrey or Mexico City company reporting to Dallas, an English file with no photo is usually safer. Photo by country: [photo on the CV](/blog/foto-en-el-cv).',
        },
        {
          type: 'p',
          text: '“Hoja de vida” in a subject line to a Mexican recruiter is not a serious error; “currículum” fits better. What is an error is a Canva infographic with the phone as an icon: Mexican parsers use the same rules. See [icons on the CV](/blog/iconos-en-el-cv).',
        },
        { type: 'h2', text: 'Spain: currículum, no DNI, CEFR' },
        {
          type: 'p',
          text: 'In Spain say currículum or CV. Do not put DNI or date of birth on the PDF. Languages as A1–C2, not “fluent English”. Photo: still expected in many local processes; at global consultancies and UK ads, no. Field guide: [what not to put on a CV](/blog/que-no-poner-en-el-cv). If your Colombian hoja de vida still has marital status, ID, and three pages, trim before InfoJobs. Spanish ATS is not searching for “hoja de vida”; it is searching for Experience, Education, email as text.',
        },
        {
          type: 'p',
          text: 'Phone: international prefix (+57, +52, +34). City: “Bogotá, willing to relocate to Madrid” is a useful line; the street is not. LinkedIn as a text URL, not a QR.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'The name changes. The parser does not',
          lead: 'It does not matter if the ad says hoja de vida or currículum. If the PDF has columns, an ID number, or icons, it fails in Bogotá and in Madrid. Regenerate a parseable version of the file you already have.',
          from: 'Hoja de vida with ID number',
          to: 'A CV / HV that can be read',
          button: 'Adapt my file',
        },
        { type: 'h2', text: 'Quick map: what travels and what stays' },
        {
          type: 'p',
          text: 'Same achievement and date content. Change the header and the extras. Before sending to another country, run this list on the PDF you already have — you do not need a new document from scratch.',
        },
        {
          type: 'ul',
          items: [
            'Document name: whatever the ad uses (hoja de vida / currículum / CV / résumé).',
            'ID: cédula, DNI, RFC, CURP — off the private PDF; yes on forms or an official public form.',
            'Photo: often yes in LatAm and Spain; no in US/UK/Canada. An old photo hurts more than none.',
            'Marital status, children, religion: out in every corporate destination.',
            'Languages: CEFR (A1–C2) in Europe and at multilatinas; “fluent” alone is not enough in Spain.',
            'Length: 1 page junior; 2 senior. The official public form can be longer because the spec says so.',
            'Page size: A4 for Spain and LatAm. Letter if the process is fully US.',
          ],
        },
        {
          type: 'p',
          text: 'Before (private Colombian HV into a Madrid ATS): large photo, ID number, “salary 4.5 M”, three Canva columns. After: Ana López, Madrid (reloc. from Bogotá), phone +57…, LinkedIn; Experience with figures; English B2; one-column PDF. The facts did not change. What the filter and the bias see first did.',
        },
        { type: 'h2', text: 'How to adapt the file you already have (15 minutes)' },
        {
          type: 'p',
          text: 'Open the PDF. Notepad test: name, email, three bullets. If text does not appear, the destination does not matter: make it parseable first ([ATS hoja de vida](/blog/hoja-de-vida-ats)). Then delete ID and marital status, adjust the photo, add a prefix, rename to Nombre_Apellido_Puesto.pdf. If the ad is in another language, do not blind-translate with Google: align titles to the destination market and keep figures. EN/ES detail: the CV language guide.',
        },
        {
          type: 'p',
          text: 'In ClarCV you upload the Word or PDF you already use at home. Analysis flags clarity and structure friction (not a magic percentage against a named ATS). You apply an A4 template with standard headings and download. Start free: 3 analyses and 2 downloads / 30 days, no card. Pro €7/month. You confirm ID and photo: the tool must not invent a DNI or a nationality.',
        },
        { type: 'h2', text: 'Boards: Computrabajo, InfoJobs, OCC, LinkedIn' },
        {
          type: 'p',
          text: 'Each board asks for “its” word. The attachment should be the same kind of clean file. LinkedIn Easy Apply uses the PDF you upload to the profile: keep it parseable, not the fair Canva. If Colombian public employment wants the unique form, do not send the modern CV “just in case” as the only file: send what the spec names. More on which file goes to which channel: [CVs for InfoJobs, LinkedIn, and job boards](/blog/cv-para-portales-empleo).',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Do hoja de vida and currículum have a different structure?',
              a: 'In private hiring, they should not. Experience, education, skills, languages. The difference is usually template folklore (ID, photo, tables), not the document type.',
            },
            {
              q: 'Can I use the same hoja de vida in Colombia and Spain?',
              a: 'The achievements yes. The header no: drop the ID, adjust photo and phone, name the file CV if the ad is Spanish. The parser is the same kind of software.',
            },
            {
              q: 'Does Colombia’s unique form work for a private company?',
              a: 'Almost never the best option. Keep it for when they require it. For private hiring, a one-column parseable PDF.',
            },
            {
              q: 'Should I put RFC on a Mexican CV?',
              a: 'Not on the PDF that circulates. Onboarding will ask later. Same as DNI in Spain.',
            },
            {
              q: 'Does ClarCV generate the SIGEP form?',
              a: 'No. It generates A4 CV/HV templates for private processes and ATS. The official public form is defined by the administration; do not replace it.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
];
